import { Injectable } from '@angular/core';
import { ResettableStore } from '../interfaces/store-reset.interface';

/**
 * A QME (re)generation the user triggered but that hasn't been confirmed
 * finished yet. Held here (root singleton) rather than in the QME report
 * component because that component is destroyed whenever the Report tab isn't
 * selected — so a marker kept only in component state is lost the moment the
 * user switches to the AI Scribe tab to edit the transcription summary, or
 * uploads a document, mid-regeneration.
 */
interface RegenerationMarker {
  /** Wall-clock ms when the regeneration was triggered (safety-expiry basis). */
  startedAtMs: number;
  /**
   * The server-side `updatedAt`/`createdAt` of the qme_report generation-status
   * row as observed *just before* triggering. Compared by identity against the
   * live value to tell "a newer status was written since we started" (i.e. the
   * run finished/failed, possibly while this tab was unmounted) from a stale
   * pre-trigger status still sitting in the client. Server-vs-server comparison,
   * so it is immune to client/server clock skew.
   */
  baselineStatusStamp: string | null;
}

@Injectable({ providedIn: 'root' })
export class QmeReportStateService implements ResettableStore {
  private readonly sessionsWithConfirmedQmeReport = new Set<string>();
  private readonly sessionsWithStartedGeneration = new Set<string>();
  private readonly regeneratingSessions = new Map<string, RegenerationMarker>();
  /**
   * Epoch-ms of when the last QME generation was *triggered* for a session.
   * Unlike {@link regeneratingSessions} this is NOT cleared when the run
   * finishes — it stays as the baseline for "were any inputs (AI Scribe edits,
   * documents, previsit answers) added since generation began?". Inputs saved
   * *while a run was in progress* land with a timestamp before the report's
   * final `updatedAt`, so using the report's save time as the baseline would
   * miss them; using the generation-start time catches them. Persisted so it
   * survives leaving/re-entering the session or a full refresh — otherwise the
   * Regenerate button never re-enables for an edit made during the prior run.
   */
  private readonly lastGenerationStartedAt = new Map<string, number>();

  /**
   * Upper bound on how long a regeneration marker is trusted. Past this, the
   * button is released even without a completion signal, so a dropped WebSocket
   * update or a crashed worker that never wrote a terminal status can never
   * wedge Regenerate disabled forever.
   */
  private static readonly REGENERATION_MAX_AGE_MS = 20 * 60 * 1000;

  /** localStorage key prefix for the per-session regeneration marker. */
  private static readonly REGEN_STORAGE_PREFIX = 'qmeRegenerating:';
  /** localStorage key prefix for the per-session generation-start baseline. */
  private static readonly LAST_GEN_STORAGE_PREFIX = 'qmeLastGenStartedAt:';

  constructor() {
    // Regeneration state is also persisted to localStorage so the "in-progress"
    // state (a disabled Regenerate button) and the generation-start baseline
    // (which drives whether new inputs re-enable it) survive a full page
    // refresh, not just tab switches. Rehydrate on boot, dropping any
    // in-progress marker that has already exceeded the safety window.
    this.hydrateRegeneratingFromStorage();
    this.hydrateLastGenerationStartedFromStorage();
  }

  hasConfirmedQmeReport(sessionId: string): boolean {
    return this.sessionsWithConfirmedQmeReport.has(sessionId);
  }

  markConfirmedQmeReport(sessionId: string): void {
    this.sessionsWithConfirmedQmeReport.add(sessionId);
    this.sessionsWithStartedGeneration.delete(sessionId);
  }

  hasStartedGeneration(sessionId: string): boolean {
    return this.sessionsWithStartedGeneration.has(sessionId);
  }

  markStartedGeneration(sessionId: string): void {
    this.sessionsWithStartedGeneration.add(sessionId);
  }

  clearStartedGeneration(sessionId: string): void {
    this.sessionsWithStartedGeneration.delete(sessionId);
  }

  /** Record that a QME (re)generation was just triggered for this session. */
  markRegenerating(
    sessionId: string,
    baselineStatusStamp: string | null,
  ): void {
    const marker: RegenerationMarker = {
      startedAtMs: Date.now(),
      baselineStatusStamp,
    };
    this.regeneratingSessions.set(sessionId, marker);
    this.persistRegenerating(sessionId, marker);
  }

  /** Drop the marker once the regeneration has completed, failed, or expired. */
  clearRegenerating(sessionId: string): void {
    this.regeneratingSessions.delete(sessionId);
    this.removePersistedRegenerating(sessionId);
  }

  /** The active regeneration marker for this session, if any. */
  getRegenerating(sessionId: string): RegenerationMarker | undefined {
    return this.regeneratingSessions.get(sessionId);
  }

  /**
   * True when a marker exists but is older than {@link REGENERATION_MAX_AGE_MS}
   * — the caller should release the button rather than trust a stale marker.
   */
  isRegenerationStale(sessionId: string): boolean {
    const marker = this.regeneratingSessions.get(sessionId);
    return (
      !!marker &&
      Date.now() - marker.startedAtMs >
        QmeReportStateService.REGENERATION_MAX_AGE_MS
    );
  }

  /** Record when the last QME generation was triggered for a session. */
  setLastGenerationStartedAt(sessionId: string, startedAtMs: number): void {
    this.lastGenerationStartedAt.set(sessionId, startedAtMs);
    try {
      localStorage.setItem(
        this.lastGenStorageKey(sessionId),
        String(startedAtMs),
      );
    } catch {
      // Private mode / quota: in-memory only (survives navigation, not refresh).
    }
  }

  /** Epoch-ms of the last triggered generation for a session, if known. */
  getLastGenerationStartedAt(sessionId: string): number | undefined {
    return this.lastGenerationStartedAt.get(sessionId);
  }

  reset(): void {
    this.sessionsWithConfirmedQmeReport.clear();
    this.sessionsWithStartedGeneration.clear();
    for (const sessionId of this.regeneratingSessions.keys()) {
      this.removePersistedRegenerating(sessionId);
    }
    this.regeneratingSessions.clear();
    for (const sessionId of this.lastGenerationStartedAt.keys()) {
      try {
        localStorage.removeItem(this.lastGenStorageKey(sessionId));
      } catch {
        // Ignore storage access errors.
      }
    }
    this.lastGenerationStartedAt.clear();
  }

  private storageKey(sessionId: string): string {
    return QmeReportStateService.REGEN_STORAGE_PREFIX + sessionId;
  }

  private lastGenStorageKey(sessionId: string): string {
    return QmeReportStateService.LAST_GEN_STORAGE_PREFIX + sessionId;
  }

  private persistRegenerating(
    sessionId: string,
    marker: RegenerationMarker,
  ): void {
    try {
      localStorage.setItem(this.storageKey(sessionId), JSON.stringify(marker));
    } catch {
      // Private mode / quota: fall back to in-memory only. The marker still
      // works for the current tab; it just won't survive a hard refresh.
    }
  }

  private removePersistedRegenerating(sessionId: string): void {
    try {
      localStorage.removeItem(this.storageKey(sessionId));
    } catch {
      // Ignore storage access errors (see persistRegenerating).
    }
  }

  /** Load persisted markers on boot, discarding any past the safety window. */
  private hydrateRegeneratingFromStorage(): void {
    let keys: string[];
    try {
      keys = Object.keys(localStorage);
    } catch {
      return;
    }

    for (const key of keys) {
      if (!key.startsWith(QmeReportStateService.REGEN_STORAGE_PREFIX)) continue;
      const sessionId = key.slice(
        QmeReportStateService.REGEN_STORAGE_PREFIX.length,
      );
      try {
        const raw = localStorage.getItem(key);
        const parsed = raw
          ? (JSON.parse(raw) as Partial<RegenerationMarker>)
          : null;
        const startedAtMs =
          typeof parsed?.startedAtMs === 'number' ? parsed.startedAtMs : null;
        const isExpired =
          startedAtMs === null ||
          Date.now() - startedAtMs >
            QmeReportStateService.REGENERATION_MAX_AGE_MS;
        if (isExpired) {
          this.removePersistedRegenerating(sessionId);
          continue;
        }
        this.regeneratingSessions.set(sessionId, {
          startedAtMs,
          baselineStatusStamp:
            typeof parsed?.baselineStatusStamp === 'string'
              ? parsed.baselineStatusStamp
              : null,
        });
      } catch {
        this.removePersistedRegenerating(sessionId);
      }
    }
  }

  /** Load persisted generation-start baselines on boot. */
  private hydrateLastGenerationStartedFromStorage(): void {
    let keys: string[];
    try {
      keys = Object.keys(localStorage);
    } catch {
      return;
    }

    for (const key of keys) {
      if (!key.startsWith(QmeReportStateService.LAST_GEN_STORAGE_PREFIX)) {
        continue;
      }
      const sessionId = key.slice(
        QmeReportStateService.LAST_GEN_STORAGE_PREFIX.length,
      );
      try {
        const raw = localStorage.getItem(key);
        const startedAtMs = raw != null ? Number(raw) : NaN;
        if (Number.isFinite(startedAtMs)) {
          this.lastGenerationStartedAt.set(sessionId, startedAtMs);
        } else {
          localStorage.removeItem(key);
        }
      } catch {
        // Ignore malformed / inaccessible entries.
      }
    }
  }
}
